/**
 * Shared Shiki highlighter for the CSK code surfaces.
 *
 * Pre-loads only the grammars that CSK actually uses (Agda, TypeScript,
 * TSX, Python, Haskell) so the bundled Shiki tree stays small even
 * though the upstream package ships ~700 grammars. The `agda` grammar
 * is shipped locally because the upstream Shiki bundle dropped Agda in
 * the 2.x → 4.x split — the canonical TextMate grammar lives in
 * `grammars/agda.tmLanguage.json` and is registered by name so callers
 * can request `lang: "agda"` exactly as they would for any built-in
 * grammar.
 *
 * The highlighter is created lazily at first request and cached as a
 * module-level singleton so subsequent renders share an Oniguruma
 * engine, the loaded themes, and the parsed grammars.
 *
 * The JavaScript regex engine is selected over Oniguruma so the
 * highlighter loads in jsdom (vitest) and on SSR runtimes that do not
 * support the WASM payload. The CSK grammars do not rely on Oniguruma's
 * extended regex features.
 *
 * SPDX-License-Identifier: LicenseRef-Proprietary
 * Copyright (c) 2026 Invariant Holdings LLC
 */

import { createHighlighterCore, type HighlighterCore } from "shiki/core";
import { createJavaScriptRegexEngine } from "shiki/engine/javascript";
import codeLanguageSupport from "./code-language-support.data.json" with { type: "json" };
import agdaGrammar from "./grammars/agda.tmLanguage.json" with { type: "json" };

export type SupportedCodeLanguage =
  | "agda"
  | "typescript"
  | "tsx"
  | "python"
  | "haskell"
  | "sql"
  | "text";

export const SUPPORTED_CODE_LANGUAGES =
  codeLanguageSupport.supportedLanguages as readonly SupportedCodeLanguage[];

/**
 * Aliases accepted by {@link normalizeCodeLanguage}. Mirrors Shiki's
 * upstream alias map for the languages CSK actually loads, plus the
 * legacy `text` fallback. Keeping the alias map small avoids
 * accidentally promising support for grammars we did not preload.
 */
export const CODE_LANGUAGE_ALIASES = codeLanguageSupport.aliases as Readonly<
  Record<string, SupportedCodeLanguage>
>;

export const SUPPORTED_CODE_THEMES = ["github-light-default", "github-dark-default"] as const;

export type SupportedCodeTheme = (typeof SUPPORTED_CODE_THEMES)[number];

let highlighterPromise: Promise<HighlighterCore> | null = null;

async function buildHighlighter(): Promise<HighlighterCore> {
  // Dynamic imports keep each grammar / theme in its own chunk so the
  // bundler can split them off the initial CodeBlock load. Awaiting in
  // parallel avoids a serial waterfall during the first highlight call.
  const [light, dark, typescript, tsx, python, haskell, sql] = await Promise.all([
    import("@shikijs/themes/github-light-default"),
    import("@shikijs/themes/github-dark-default"),
    import("@shikijs/langs/typescript"),
    import("@shikijs/langs/tsx"),
    import("@shikijs/langs/python"),
    import("@shikijs/langs/haskell"),
    import("@shikijs/langs/sql"),
  ]);

  return await createHighlighterCore({
    themes: [light.default, dark.default],
    langs: [
      typescript.default,
      tsx.default,
      python.default,
      haskell.default,
      sql.default,
      // Locally vendored Agda TextMate grammar — the JSON shape
      // satisfies `LanguageRegistration` and Shiki's `langs` accepts
      // both inline objects and dynamic-import functions.
      agdaGrammar,
    ],
    engine: createJavaScriptRegexEngine({ forgiving: true }),
  });
}

/** Get (lazily creating) the shared CSK highlighter singleton. */
export async function getCodeHighlighter(): Promise<HighlighterCore> {
  highlighterPromise ??= buildHighlighter();
  return highlighterPromise;
}

/**
 * Reset the cached highlighter — exists so tests can run in isolation
 * without state bleed across files.
 */
export function resetCodeHighlighterForTesting(): void {
  highlighterPromise = null;
}

/** Whether a string is one of the CSK-supported language identifiers. */
export function isSupportedCodeLanguage(value: string): value is SupportedCodeLanguage {
  return (SUPPORTED_CODE_LANGUAGES as readonly string[]).includes(value);
}

/**
 * Normalise a language identifier (`"ts"`, `"py"`, `"hs"`, …) to the
 * canonical {@link SupportedCodeLanguage} the highlighter recognises.
 * Returns `"text"` for unknown values so the CodeBlock still renders
 * the source verbatim instead of throwing.
 */
export function normalizeCodeLanguage(value: string | undefined): SupportedCodeLanguage {
  if (value === undefined) return "text";
  const lookup = CODE_LANGUAGE_ALIASES[value.toLowerCase()];
  return lookup ?? "text";
}
